import { useForm } from "@tanstack/react-form";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { RegistryEpisode } from "../../registry";
import { useCreateEpisode, useUpdateEpisode } from "./mutations";

function hasErrors(field: { state: { meta: { errors: unknown[] } } }) {
  return field.state.meta.errors.length > 0;
}

interface EpisodeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seriesPk: number;
  episode?: RegistryEpisode;
}

export function EpisodeFormDialog({
  open,
  onOpenChange,
  seriesPk,
  episode,
}: EpisodeFormDialogProps) {
  const isEditing = !!episode;
  const createMutation = useCreateEpisode();
  const updateMutation = useUpdateEpisode();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;
  const coverInputRef = useRef<HTMLInputElement>(null);
  const playerImageInputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues: {
      label: "",
      summary: "",
      coverFile: null as File | null,
      playerImageFile: null as File | null,
    },
    onSubmit: ({ value }) => {
      const formData = new FormData();
      formData.append("label", value.label);
      if (value.summary) formData.append("summary", value.summary);
      if (value.coverFile) formData.append("cover", value.coverFile);
      if (value.playerImageFile)
        formData.append("player_image", value.playerImageFile);

      if (isEditing && episode) {
        updateMutation.mutate(
          { pk: episode.pk, data: formData },
          { onSuccess: () => onOpenChange(false) },
        );
      } else {
        formData.append("series", seriesPk.toString());
        createMutation.mutate(formData, {
          onSuccess: () => onOpenChange(false),
        });
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset form state only when dialog opens
  useEffect(() => {
    if (open) {
      form.reset();
      form.setFieldValue("label", episode?.label ?? "");
      form.setFieldValue("summary", episode?.summary ?? "");
      form.setFieldValue("coverFile", null);
      form.setFieldValue("playerImageFile", null);
      if (coverInputRef.current) coverInputRef.current.value = "";
      if (playerImageInputRef.current) playerImageInputRef.current.value = "";
      createMutation.reset();
      updateMutation.reset();
    }
  }, [open, episode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Episode" : "New Episode"}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <form.Field
            name="label"
            validators={{
              onSubmit: ({ value }) =>
                !value.trim() ? "Title is required." : undefined,
            }}
          >
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium" htmlFor="episode-label">
                  Title <span className="text-destructive">*</span>
                </label>
                <Input
                  id="episode-label"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Episode title"
                  className={hasErrors(field) ? "border-destructive" : ""}
                />
                {hasErrors(field) && (
                  <p className="text-xs text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="summary">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label
                  className="text-sm font-medium"
                  htmlFor="episode-summary"
                >
                  Summary
                </label>
                <textarea
                  id="episode-summary"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Episode summary (optional)"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-ring"
                  rows={3}
                />
              </div>
            )}
          </form.Field>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="episode-cover">
              Cover
            </label>
            {isEditing && episode?.cover && (
              <form.Field name="coverFile">
                {(field) =>
                  !field.state.value && (
                    <p className="text-xs text-muted-foreground">
                      Current cover set. Upload a new file to replace it.
                    </p>
                  )
                }
              </form.Field>
            )}
            <Input
              id="episode-cover"
              ref={coverInputRef}
              type="file"
              accept="image/png"
              onChange={(e) =>
                form.setFieldValue("coverFile", e.target.files?.[0] ?? null)
              }
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              className="text-sm font-medium"
              htmlFor="episode-player-image"
            >
              Player Image
              <span className="ml-1 text-xs italic">
                &bull; Shown while the episode plays. It is baked into the
                episode package, so it reaches players only once you build a new
                bundle and publish it.
              </span>
            </label>
            {isEditing && episode?.playerImage && (
              <form.Field name="playerImageFile">
                {(field) =>
                  !field.state.value && (
                    <p className="text-xs text-muted-foreground">
                      Current player image set. Upload a new file to replace it.
                    </p>
                  )
                }
              </form.Field>
            )}
            <Input
              id="episode-player-image"
              ref={playerImageInputRef}
              type="file"
              accept="image/png"
              onChange={(e) =>
                form.setFieldValue(
                  "playerImageFile",
                  e.target.files?.[0] ?? null,
                )
              }
            />
          </div>

          {mutationError && (
            <p className="text-sm text-destructive">
              {mutationError.message || "An error occurred. Please try again."}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Episode"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
